/**
 * Hand-maintained Apollo hooks for portfolio / custom tags.
 * Keep out of graphql-codegen output so `npm run codegen` does not overwrite them.
 * Mirror the shape of operations in `src/graphql/generated/hooks.ts` for consistency.
 */
import { gql } from '@apollo/client'
import * as Apollo from '@apollo/client'

const defaultOptions = {} as const

export type ICustomTagSharedPortfolioSummary = {
  __typename?: 'SharedPortfolioSummary'
  documentId?: string | null
  name?: string | null
  slug?: string | null
}

export type ICustomTagResult = {
  __typename?: 'CustomTagResult'
  id: string
  name: string
  slug?: string | null
  solutionIds?: Array<number | null> | null
  solutionDocumentIds?: Array<string | null> | null
  portfolioId?: string | null
  isSharedPortfolio?: boolean | null
  sharedPortfolio?: ICustomTagSharedPortfolioSummary | null
}

export type IGetMyCustomTagsQueryVariables = {
  portfolioId?: string | null
  isSharedPortfolio?: boolean | null
}

export type IGetMyCustomTagsQuery = {
  __typename?: 'Query'
  getMyCustomTags: Array<ICustomTagResult>
}

export const GetMyCustomTagsDocument = gql`
  query getMyCustomTags($portfolioId: String, $isSharedPortfolio: Boolean) {
    getMyCustomTags(portfolioId: $portfolioId, isSharedPortfolio: $isSharedPortfolio) {
      id
      name
      slug
      solutionIds
      solutionDocumentIds
      portfolioId
      isSharedPortfolio
      sharedPortfolio {
        documentId
        name
        slug
      }
    }
  }
`

export function useGetMyCustomTagsQuery(
  baseOptions?: Apollo.QueryHookOptions<IGetMyCustomTagsQuery, IGetMyCustomTagsQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions }
  return Apollo.useQuery<IGetMyCustomTagsQuery, IGetMyCustomTagsQueryVariables>(GetMyCustomTagsDocument, options)
}

export function useGetMyCustomTagsLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<IGetMyCustomTagsQuery, IGetMyCustomTagsQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions }
  return Apollo.useLazyQuery<IGetMyCustomTagsQuery, IGetMyCustomTagsQueryVariables>(GetMyCustomTagsDocument, options)
}

export type ISearchCustomTagsQueryVariables = {
  keyword?: string | null
  portfolioId?: string | null
  isSharedPortfolio?: boolean | null
}

export type ISearchCustomTagsQuery = {
  __typename?: 'Query'
  searchCustomTags: Array<ICustomTagResult>
}

export const SearchCustomTagsDocument = gql`
  query searchCustomTags($keyword: String, $portfolioId: String, $isSharedPortfolio: Boolean) {
    searchCustomTags(keyword: $keyword, portfolioId: $portfolioId, isSharedPortfolio: $isSharedPortfolio) {
      id
      name
      slug
      solutionIds
      solutionDocumentIds
    }
  }
`

export function useSearchCustomTagsLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<ISearchCustomTagsQuery, ISearchCustomTagsQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions }
  return Apollo.useLazyQuery<ISearchCustomTagsQuery, ISearchCustomTagsQueryVariables>(SearchCustomTagsDocument, options)
}

export type ICreatePortfolioTagMutationVariables = { name: string; portfolioId: string }

export type ICreatePortfolioTagMutation = {
  __typename?: 'Mutation'
  createPortfolioTag?: ICustomTagResult | null
}

export const CreatePortfolioTagDocument = gql`
  mutation createPortfolioTag($name: String!, $portfolioId: String!) {
    createPortfolioTag(name: $name, portfolioId: $portfolioId) {
      id
      name
      slug
      solutionIds
    }
  }
`

export function useCreatePortfolioTagMutation(
  baseOptions?: Apollo.MutationHookOptions<ICreatePortfolioTagMutation, ICreatePortfolioTagMutationVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions }
  return Apollo.useMutation<ICreatePortfolioTagMutation, ICreatePortfolioTagMutationVariables>(
    CreatePortfolioTagDocument,
    options,
  )
}

export type IEditPortfolioTagMutationVariables = {
  tagDocumentId: string
  name: string
  sharedPortfolioId?: string | null
}

export type IEditPortfolioTagMutation = {
  __typename?: 'Mutation'
  editPortfolioTag?: ICustomTagResult | null
}

export const EditPortfolioTagDocument = gql`
  mutation editPortfolioTag($tagDocumentId: ID!, $name: String!, $sharedPortfolioId: ID) {
    editPortfolioTag(tagDocumentId: $tagDocumentId, name: $name, sharedPortfolioId: $sharedPortfolioId) {
      id
      name
      slug
      solutionIds
      solutionDocumentIds
    }
  }
`

export function useEditPortfolioTagMutation(
  baseOptions?: Apollo.MutationHookOptions<IEditPortfolioTagMutation, IEditPortfolioTagMutationVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions }
  return Apollo.useMutation<IEditPortfolioTagMutation, IEditPortfolioTagMutationVariables>(
    EditPortfolioTagDocument,
    options,
  )
}

export type IDeletePortfolioTagMutationVariables = { tagDocumentId: string }

export type IDeletePortfolioTagMutation = {
  __typename?: 'Mutation'
  deletePortfolioTag?: boolean | null
}

export const DeletePortfolioTagDocument = gql`
  mutation deletePortfolioTag($tagDocumentId: ID!) {
    deletePortfolioTag(tagDocumentId: $tagDocumentId)
  }
`

export function useDeletePortfolioTagMutation(
  baseOptions?: Apollo.MutationHookOptions<IDeletePortfolioTagMutation, IDeletePortfolioTagMutationVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions }
  return Apollo.useMutation<IDeletePortfolioTagMutation, IDeletePortfolioTagMutationVariables>(
    DeletePortfolioTagDocument,
    options,
  )
}

export type IAddPortfolioTagToSolutionMutationVariables = {
  tagDocumentId: string
  solutionId?: number | null
  solutionDocumentId?: string | null
}

export type IAddPortfolioTagToSolutionMutation = {
  __typename?: 'Mutation'
  addPortfolioTagToSolution?: boolean | null
}

export const AddPortfolioTagToSolutionDocument = gql`
  mutation addPortfolioTagToSolution($tagDocumentId: ID!, $solutionId: Int, $solutionDocumentId: ID) {
    addPortfolioTagToSolution(
      tagDocumentId: $tagDocumentId
      solutionId: $solutionId
      solutionDocumentId: $solutionDocumentId
    )
  }
`

export function useAddPortfolioTagToSolutionMutation(
  baseOptions?: Apollo.MutationHookOptions<
    IAddPortfolioTagToSolutionMutation,
    IAddPortfolioTagToSolutionMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions }
  return Apollo.useMutation<IAddPortfolioTagToSolutionMutation, IAddPortfolioTagToSolutionMutationVariables>(
    AddPortfolioTagToSolutionDocument,
    options,
  )
}

export type IRemovePortfolioTagFromSolutionMutationVariables = {
  tagDocumentId: string
  solutionId?: number | null
  solutionDocumentId?: string | null
}

export type IRemovePortfolioTagFromSolutionMutation = {
  __typename?: 'Mutation'
  removePortfolioTagFromSolution?: boolean | null
}

export const RemovePortfolioTagFromSolutionDocument = gql`
  mutation removePortfolioTagFromSolution($tagDocumentId: ID!, $solutionId: Int, $solutionDocumentId: ID) {
    removePortfolioTagFromSolution(
      tagDocumentId: $tagDocumentId
      solutionId: $solutionId
      solutionDocumentId: $solutionDocumentId
    )
  }
`

export function useRemovePortfolioTagFromSolutionMutation(
  baseOptions?: Apollo.MutationHookOptions<
    IRemovePortfolioTagFromSolutionMutation,
    IRemovePortfolioTagFromSolutionMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions }
  return Apollo.useMutation<IRemovePortfolioTagFromSolutionMutation, IRemovePortfolioTagFromSolutionMutationVariables>(
    RemovePortfolioTagFromSolutionDocument,
    options,
  )
}
